/**
 * OCC FP checker（halo 人工 ROI）的 case 级问题清单。
 *
 * 这张表是排障清单，不是统计汇总表：一行一个 case，回答「哪个 case 失败、
 * 偶发还是连续、发生在哪几帧/哪几秒」。所以不复用 Benchmark 的分类别指标表，
 * 也不做加号展开——OCC FP 的最小展示粒度已经是 case。
 *
 * 数据来自 ``/perception/report/checker-cases`` 补充的 ``extras.halo_metrics``
 * 字段；老结果（无 halo_metrics）仍能渲染，结构化列显示 ``EMPTY``。
 *
 * 状态口径：
 * - ``error``   → 不通过（红色）
 * - ``warning`` → 通过但有告警（橙色；FalseDetection 连续误检 ≤ 阈值）
 * - ``ok``      → 通过（绿色）
 * - ``skip``    → 未评估（灰色，不进通过率分母）
 */
import { useEffect, useState } from 'react';
import { App, Button, Collapse, Image, Space, Switch, Table, Tag, Tooltip } from 'antd';
import { FileTextOutlined, LinkOutlined, PictureOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { getEvaluationFileUrls, type PerceptionCheckerCaseRow } from '@/apis/simulation';
import ApiCodeLink from '@components/ApiCodeLink';
import { buildFoxgloveUrl, resolveMcapUrl } from '@/common/foxglove';
import { sceneGroupDisplay } from '@/constants/sceneGroup';

import { EMPTY, fmtCount, fmtRatio } from '../format';

const UNLABELED = '__unlabeled__';
/** 失败时间段胶囊最多直接展示的段数，超出折叠为 +N */
const MAX_TIME_TAGS = 2;

/** halo 上报的 "start-end"（Unix 秒字符串）→ 相对 case 起点的秒数。 */
const relativeRangeText = (range: string, gtTsMin: number | null | undefined): string => {
  if (gtTsMin === null || gtTsMin === undefined) return range;
  const parts = range.split('-');
  if (parts.length !== 2) return range;
  const start = Number(parts[0]);
  const end = Number(parts[1]);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return range;
  const fmt = (v: number) => `${(v - gtTsMin).toFixed(1)}s`;
  return `${fmt(start)} ~ ${fmt(end)}`;
};
/** 任务级 preview_image/preview.webp 的签名 URL；404 / 过期时走内置占位图。 */
const PREVIEW_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="128" height="72"><rect width="100%" height="100%" fill="#f2f3f5"/><text x="50%" y="50%" fill="#9b9da3" font-size="12" text-anchor="middle" dominant-baseline="middle">暂无预览</text></svg>',
  );

/** 指标说明：小字灰色，一展开全量显示，每条独立一行。 */
const METRIC_HELP_LINES = [
  '有效帧：OCC 输出帧与标注帧成功对齐、且 ROI 落在评测范围内的帧数；未对齐、ROI 过近被过滤、GT 时间段外的帧不计入。',
  '失败帧：有效帧中 ROI 内出现占据的帧数。栅格版（occ_false_detection_checker / occ_road_false_checker）看 /perception/occupancy，1 个 OCCUPIED voxel 即失败；障碍物版（occ_obstacle_*）看 /fusion/fused_occ_obstacles，1 个与 ROI 相交的障碍物多边形即失败。',
  '失败率（单 case）= 失败帧 / 有效帧；总览的失败率 = Σ失败帧 / Σ有效帧（Job 级合并口径，不是各 case 比率的平均）。',
  '最长连续（x / y）：x 为该 case 实际最长连续失败帧数，y 为 checker 配置阈值；x > y 红色加粗，0 < x ≤ y 橙色，x = 0 灰色。',
  '阈值默认值：occ_false_detection_checker / occ_obstacle_false_detection_checker 连续 > 2 判 error；occ_road_false_checker / occ_obstacle_road_false_checker 0 容忍，任一失败帧即 error。',
  '失败时间段：连续失败帧合并后的时间区间，已换算成相对该 case 第 1 个标注帧（case 视频起点）的秒数；hover 可查看原始 Unix 时间戳。',
  '证据图：点失败时间段（带图标的红色标签）或行尾「图 N」，看该段失败帧的俯视图：失败的 ROI、框内计入的 OCCUPIED 格（按类别着色）或相交的 OCC 障碍物；每个 case 最多 20 张。',
  '状态：ok 通过（绿色）；warning 通过但有告警（橙色）；error 不通过（红色）；skip 未评估（灰色）。',
  '通过率 = ok / (ok + error)；warning、skip 单独计数，不进分子也不进分母（总览「主要指标」列给出四种状态的 case 数）。',
];

interface OccRoiCheckerCaseTableProps {
  rows: PerceptionCheckerCaseRow[];
  loading?: boolean;
  /** 打开 Task 日志 Modal（来自 useTaskLogModal） */
  onViewLog: (taskId: number) => void;
}

async function loadPreviewUrls(rows: PerceptionCheckerCaseRow[]): Promise<Record<number, string>> {
  const taskIds = Array.from(
    new Set(rows.map((r) => r.taskId).filter((x): x is number => x != null)),
  );
  if (taskIds.length === 0) return {};
  const files = await getEvaluationFileUrls(
    taskIds.map((id) => `${id}/preview_image/preview.webp`),
    'output',
    { skipErrorHandler: true },
  );
  const map: Record<number, string> = {};
  files.forEach((f) => {
    const id = Number(f.path.split('/', 1)[0]);
    if (id && f.url) map[id] = f.url;
  });
  return map;
}

const statusTag = (r: PerceptionCheckerCaseRow) => {
  if (!r.passed) return <Tag color="red" bordered={false}>不通过</Tag>;
  if (r.status === 'warning') return <Tag color="orange" bordered={false}>通过（告警）</Tag>;
  if (r.status === 'skip') return <Tooltip title="未评估 / 数据不足，不进通过率分母"><Tag bordered={false}>未评估</Tag></Tooltip>;
  return <Tag color="green" bordered={false}>通过</Tag>;
};

const consecutiveCell = (r: PerceptionCheckerCaseRow) => {
  const v = r.maxConsecutiveFail;
  if (v === null || v === undefined) return EMPTY;
  const threshold = r.consecutiveThreshold;
  const exceeded = threshold !== null && threshold !== undefined && v > threshold;
  const color = exceeded ? '#e03131' : v > 0 ? '#f08c00' : undefined;
  return (
    <span style={{ color, fontWeight: exceeded ? 600 : 400 }}>
      {fmtCount(v)}
      {threshold !== null && threshold !== undefined ? ` / ${threshold}` : ''}
    </span>
  );
};

/** 某失败时间段（"start-end" Unix 秒）内第一张证据图的下标；没有返回 -1。 */
const firstImageInRange = (r: PerceptionCheckerCaseRow, range: string): number => {
  const [start, end] = range.split('-').map(Number);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return -1;
  // 时间段是逐帧时间戳的合并，两端各放 50ms 容差
  return (r.evidenceImages ?? []).findIndex(
    (x) => x.gtTs !== null && x.gtTs >= start - 0.05 && x.gtTs <= end + 0.05,
  );
};

const timeRangeCell = (
  r: PerceptionCheckerCaseRow,
  onOpenEvidence: (r: PerceptionCheckerCaseRow, index: number) => void,
) => {
  const ranges = r.failTimeRanges;
  if (!ranges || ranges.length === 0) return EMPTY;
  const relative = ranges.map((x) => relativeRangeText(x, r.gtTsMin));
  const shown = ranges.slice(0, MAX_TIME_TAGS);
  const rest = ranges.length - shown.length;
  const shownRelative = relative.slice(0, MAX_TIME_TAGS);
  const images = r.evidenceImages ?? [];
  return (
    <Space size={4} wrap>
      {shownRelative.map((x, i) => {
        const at = firstImageInRange(r, shown[i]);
        return (
          <Tooltip
            key={i}
            title={`相对时间：${x}\n绝对时间戳：${shown[i]}${at >= 0 ? '\n点击查看该段失败帧的俯视图' : ''}`}
          >
            <Tag
              color="red"
              bordered={false}
              style={{ marginInlineEnd: 0, cursor: at >= 0 ? 'pointer' : undefined }}
              icon={at >= 0 ? <PictureOutlined /> : undefined}
              onClick={at >= 0 ? () => onOpenEvidence(r, at) : undefined}
            >
              {x}
            </Tag>
          </Tooltip>
        );
      })}
      {rest > 0 && <Tooltip title={relative.join('\n')}><Tag bordered={false} style={{ marginInlineEnd: 0 }}>+{rest}</Tag></Tooltip>}
      {images.length > 0 && (
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onOpenEvidence(r, 0)}>
          图 {images.length}
        </Button>
      )}
    </Space>
  );
};

const roiClassCell = (r: PerceptionCheckerCaseRow) => {
  const classes = r.roiClasses;
  if (!classes || classes.length === 0) return EMPTY;
  return (
    <Space size={4} wrap>
      {classes.map((c) => (
        <Tag key={c} bordered={false} style={{ marginInlineEnd: 0 }}>{c}</Tag>
      ))}
    </Space>
  );
};

export default function OccRoiCheckerCaseTable({
  rows, loading, onViewLog,
}: OccRoiCheckerCaseTableProps) {
  const { message } = App.useApp();
  const [showAll, setShowAll] = useState(false);
  const [foxgloveLoadingKey, setFoxgloveLoadingKey] = useState<string | null>(null);
  const [previewUrlMap, setPreviewUrlMap] = useState<Record<number, string>>({});
  const [previewLoading, setPreviewLoading] = useState(false);
  /** 正在查看的证据图（已签名 URL）与当前张 */
  const [evidence, setEvidence] = useState<{ urls: string[]; current: number } | null>(null);

  useEffect(() => {
    let alive = true;
    setPreviewUrlMap({});
    setPreviewLoading(true);
    loadPreviewUrls(rows)
      .then((map) => {
        if (alive) setPreviewUrlMap(map);
      })
      .catch(() => {
        if (alive) setPreviewUrlMap({});
      })
      .finally(() => {
        if (alive) setPreviewLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [rows]);

  const failCount = rows.filter((r) => !r.passed).length;
  const data = showAll ? rows : rows.filter((r) => !r.passed);

  const openFoxglove = async (row: PerceptionCheckerCaseRow) => {
    if (!row.taskId || !row.caseId) return;
    const key = String(row.taskId);
    setFoxgloveLoadingKey(key);
    try {
      const url = await resolveMcapUrl(row.taskId, row.caseId);
      if (!url) {
        message.warning('未找到对应的 mcap 文件');
        return;
      }
      window.open(buildFoxgloveUrl(url, row.taskId), '_blank');
    } catch {
      message.error('获取 mcap 文件链接失败');
    } finally {
      setFoxgloveLoadingKey(null);
    }
  };

  const openEvidence = async (row: PerceptionCheckerCaseRow, index: number) => {
    const images = row.evidenceImages ?? [];
    if (!row.taskId || images.length === 0) return;
    try {
      const files = await getEvaluationFileUrls(
        images.map((x) => `${row.taskId}/${x.path}`),
        'output',
        { skipErrorHandler: true },
      );
      const byPath = new Map(files.map((f) => [f.path, f.url]));
      const urls = images
        .map((x) => byPath.get(`${row.taskId}/${x.path}`))
        .filter((u): u is string => !!u);
      if (urls.length === 0) {
        message.warning('证据图不存在或已过期（task 产物保留 30 天）');
        return;
      }
      setEvidence({ urls, current: Math.min(index, urls.length - 1) });
    } catch {
      message.error('获取证据图链接失败');
    }
  };

  const columns: ColumnsType<PerceptionCheckerCaseRow> = [
    { title: 'Case', dataIndex: 'caseId', key: 'caseId', width: 160, fixed: 'left',
      render: (v: string | null, r) => (
        <Tooltip title={r.occSceneId ? `GT 场景：${r.occSceneId}` : undefined}>
          {v || EMPTY}
        </Tooltip>
      ) },
    { title: 'API_CODE', dataIndex: 'apiCode', key: 'apiCode', width: 130,
      render: (v: string | null, r) => <ApiCodeLink code={v} source={r.source} /> },
    { title: '场景分组', dataIndex: 'sceneGroup', key: 'sceneGroup', width: 120,
      render: (v: string | null) => sceneGroupDisplay(v) },
    { title: '场景标签', dataIndex: 'label', key: 'label', width: 130, ellipsis: true,
      render: (v: string | null) => (!v || v === UNLABELED ? EMPTY : v) },
    { title: 'ROI 类别', key: 'roiClasses', width: 130, render: roiClassCell },
    { title: '预览', key: 'preview', width: 140,
      render: (_, r) => {
        const url = r.taskId ? previewUrlMap[r.taskId] : undefined;
        if (!url) {
          return (
            <span style={{ color: '#9b9da3', fontSize: 12 }}>
              {previewLoading ? '加载中…' : '暂无预览'}
            </span>
          );
        }
        return <Image src={url} width={128} height={72} preview={false} fallback={PREVIEW_FALLBACK} />;
      } },
    { title: '有效帧', dataIndex: 'eligibleFrames', key: 'eligibleFrames', width: 80, align: 'right', render: fmtCount },
    { title: '失败帧', dataIndex: 'failedFrames', key: 'failedFrames', width: 80, align: 'right',
      render: (v: number | null) => (
        <span style={{ color: v ? '#e03131' : undefined }}>{fmtCount(v)}</span>
      ) },
    { title: '失败率', dataIndex: 'failRate', key: 'failRate', width: 90, align: 'right',
      render: (v: number | null) => fmtRatio(v, 1) },
    { title: <Tooltip title="当前值 / 阈值；超过阈值红色加粗">最长连续</Tooltip>,
      key: 'maxConsecutiveFail', width: 120, align: 'right', render: consecutiveCell },
    { title: '失败时间段', key: 'failTimeRanges', width: 240,
      render: (_, r) => timeRangeCell(r, openEvidence) },
    { title: '状态', key: 'status', width: 110, render: statusTag },
    { title: '说明', dataIndex: 'msg', key: 'msg', width: 260, ellipsis: { showTitle: false },
      render: (v: string | null) => v ? <Tooltip title={v} placement="topLeft">{v}</Tooltip> : EMPTY },
    { title: '操作', key: 'action', width: 96, fixed: 'right',
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title={r.taskId ? '在 Foxglove 中打开回放' : '该场景无 task，无回放'}>
            <Button
              type="text" size="small" icon={<LinkOutlined />}
              disabled={!r.taskId || !r.caseId}
              loading={foxgloveLoadingKey === String(r.taskId)}
              onClick={() => openFoxglove(r)}
            />
          </Tooltip>
          <Tooltip title={r.taskId ? 'Task 日志' : '该场景无 task，无日志'}>
            <Button
              type="text" size="small" icon={<FileTextOutlined />}
              disabled={!r.taskId}
              onClick={() => r.taskId && onViewLog(r.taskId)}
            />
          </Tooltip>
        </Space>
      ) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6b6f76' }}>
        <Switch size="small" checked={showAll} onChange={setShowAll} />
        <span>
          显示全部（共 {rows.length} 个运行 case，不通过 {failCount} 个）
        </span>
      </div>
      <Table<PerceptionCheckerCaseRow>
        rowKey={(r) => `${r.sceneId ?? 'none'}-${r.taskId ?? 'none'}`}
        size="small"
        loading={loading}
        dataSource={data}
        columns={columns}
        tableLayout="fixed"
        scroll={{ x: 1500 }}
        locale={{
          emptyText: showAll ? '暂无运行 case' : '没有不通过的 case，可打开「显示全部」查看',
        }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '500'],
          showTotal: (t) => `总共 ${t} 行`,
        }}
      />
      {evidence && (
        <Image.PreviewGroup
          items={evidence.urls}
          preview={{
            visible: true,
            current: evidence.current,
            onChange: (current) => setEvidence({ ...evidence, current }),
            onVisibleChange: (visible) => {
              if (!visible) setEvidence(null);
            },
          }}
        />
      )}
      <Collapse
        ghost
        size="small"
        items={[{
          key: 'metrics-help',
          label: '指标说明',
          children: (
            <div
              style={{
                color: '#6b6f76',
                fontSize: 12,
                lineHeight: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {METRIC_HELP_LINES.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          ),
        }]}
      />
    </div>
  );
}
